const TIKTOK_CLICK_ID_KEY = 'julius_ttclid';
const TIKTOK_CLICK_ID_TTL_MS = 90 * 24 * 60 * 60 * 1000;
let pendingTikTokClickId: string | undefined;

type StoredTikTokClickId = {
  value: string;
  expiresAt: number;
};

type TikTokQueue = {
  page: () => void;
  track: (eventName: string, properties?: Record<string, unknown>) => void;
};

type TikTokWindow = Window & {
  Cookiebot?: {
    consent?: {
      marketing?: boolean;
    };
  };
  ttq?: TikTokQueue;
};

const getTikTokWindow = () => {
  if (typeof window === 'undefined') return undefined;
  return window as TikTokWindow;
};

const getStorage = () => {
  try {
    return getTikTokWindow()?.localStorage;
  } catch (_error) {
    return undefined;
  }
};

const parseStoredClickId = (value: string | null): StoredTikTokClickId | undefined => {
  if (!value) return undefined;

  try {
    const parsed: unknown = JSON.parse(value);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      !('value' in parsed) ||
      !('expiresAt' in parsed) ||
      typeof parsed.value !== 'string' ||
      typeof parsed.expiresAt !== 'number'
    ) {
      return undefined;
    }
    return { value: parsed.value, expiresAt: parsed.expiresAt };
  } catch (_error) {
    return undefined;
  }
};

export const hasTikTokMarketingConsent = () =>
  getTikTokWindow()?.Cookiebot?.consent?.marketing === true;

export const clearTikTokClickId = () => {
  getStorage()?.removeItem(TIKTOK_CLICK_ID_KEY);
};

const persistTikTokClickId = (ttclid: string) => {
  getStorage()?.setItem(
    TIKTOK_CLICK_ID_KEY,
    JSON.stringify({
      value: ttclid,
      expiresAt: Date.now() + TIKTOK_CLICK_ID_TTL_MS
    })
  );
};

export const captureTikTokClickIdFromUrl = (
  url = typeof window !== 'undefined' ? window.location.href : ''
) => {
  if (!url) return undefined;

  let ttclid: string | null = null;
  try {
    ttclid = new URL(url).searchParams.get('ttclid');
  } catch (_error) {
    return undefined;
  }

  if (!ttclid) return undefined;

  pendingTikTokClickId = ttclid;
  if (!hasTikTokMarketingConsent()) return undefined;

  persistTikTokClickId(ttclid);
  return ttclid;
};

export const getStoredTikTokClickId = () => {
  if (!hasTikTokMarketingConsent()) return undefined;

  const storage = getStorage();
  if (!storage) return undefined;

  const storedClickId = parseStoredClickId(storage.getItem(TIKTOK_CLICK_ID_KEY));
  if (!storedClickId) return undefined;

  if (storedClickId.expiresAt <= Date.now()) {
    storage.removeItem(TIKTOK_CLICK_ID_KEY);
    return undefined;
  }

  return storedClickId.value;
};

export const getTikTokClickId = () => captureTikTokClickIdFromUrl() || getStoredTikTokClickId();

export const initializeTikTokAttribution = () => {
  const tiktokWindow = getTikTokWindow();
  if (!tiktokWindow) return () => undefined;

  captureTikTokClickIdFromUrl();

  const syncClickIdWithConsent = () => {
    if (hasTikTokMarketingConsent()) {
      captureTikTokClickIdFromUrl();
      if (pendingTikTokClickId) persistTikTokClickId(pendingTikTokClickId);
    } else {
      clearTikTokClickId();
    }
  };

  if (tiktokWindow.Cookiebot?.consent) syncClickIdWithConsent();

  tiktokWindow.addEventListener('CookiebotOnConsentReady', syncClickIdWithConsent);
  tiktokWindow.addEventListener('CookiebotOnAccept', syncClickIdWithConsent);
  tiktokWindow.addEventListener('CookiebotOnDecline', syncClickIdWithConsent);

  return () => {
    tiktokWindow.removeEventListener('CookiebotOnConsentReady', syncClickIdWithConsent);
    tiktokWindow.removeEventListener('CookiebotOnAccept', syncClickIdWithConsent);
    tiktokWindow.removeEventListener('CookiebotOnDecline', syncClickIdWithConsent);
  };
};

export const trackTikTokPageView = () => {
  const tiktokWindow = getTikTokWindow();
  if (!hasTikTokMarketingConsent() || typeof tiktokWindow?.ttq?.page !== 'function') return false;

  tiktokWindow.ttq.page();
  return true;
};

export const trackTikTokEvent = (eventName: string, properties?: Record<string, unknown>) => {
  const tiktokWindow = getTikTokWindow();
  if (!hasTikTokMarketingConsent() || typeof tiktokWindow?.ttq?.track !== 'function') return false;

  tiktokWindow.ttq.track(eventName, properties);
  return true;
};

export const runOnceWithTikTokMarketingConsent = (callback: () => boolean) => {
  const tiktokWindow = getTikTokWindow();
  if (!tiktokWindow) return () => undefined;

  let hasRun = false;
  let retryAttempts = 0;
  let retryTimeout: number | undefined;
  const run = () => {
    if (!hasRun && hasTikTokMarketingConsent()) {
      hasRun = callback();
      if (!hasRun && retryAttempts < 10) {
        retryAttempts += 1;
        retryTimeout = tiktokWindow.setTimeout(run, 250);
      }
    }
  };
  const handleConsentAccepted = () => {
    retryAttempts = 0;
    run();
  };

  run();
  tiktokWindow.addEventListener('CookiebotOnAccept', handleConsentAccepted);

  return () => {
    tiktokWindow.removeEventListener('CookiebotOnAccept', handleConsentAccepted);
    if (retryTimeout !== undefined) tiktokWindow.clearTimeout(retryTimeout);
  };
};
